import { useState } from "react";
import { Item } from "../types";
import useDebounce from "../hooks/useDebounce";
import {
  useDeleteItemMutation,
  useFetchItemsQuery,
  useToggleItemStockMutation,
  useTypedSelector,
} from "../store";
import { showToast, ToastType } from "../utils/toast.utils";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import Pagination from "../components/common/Pagination";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import ToggleSwitch from "../components/common/ToggleSwitch";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn";
import TextField from "../components/common/TextField";
import { centeredTdClasses, tdClasses, thClasses } from "../configs";

const ItemsWrapper = () => {
  const restaurant = useTypedSelector((state) => state.session.restaurant);

  if (!restaurant)
    return (
      <div className="w-full flex justify-center mt-4 text-xl font-semibold">
        Veuillez sélectionner un restaurant
      </div>
    );

  return (
    <div className="w-full flex flex-col items-center justify-center">
      <Items restaurantId={restaurant._id} />
    </div>
  );
};

interface ItemProps {
  restaurantId: string;
}

const Items: React.FC<ItemProps> = ({ restaurantId }) => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setDisplayName] = useState<string>("");
  const debouncedDislayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null);

  const [deleteItem] = useDeleteItemMutation();
  const [toggleStock] = useToggleItemStockMutation();

  const confirmDelete = (itemToDelete: Item) => {
    setItemToDelete(itemToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    const result = await deleteItem(itemToDelete._id);
    if (!result.error) {
      showToast("Produit supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setItemToDelete(null);
  };

  const { data: items } = useFetchItemsQuery({
    restaurantId,
    searchQuery: {
      page,
      perPage,
      displayName: debouncedDislayName,
    },
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="flex items-center justify-between">
        <div className="w-1/4">
          <TextField
            id="lastName"
            value={displayName}
            onChange={(e) =>
              setDisplayName((e.target as HTMLInputElement).value)
            }
            placeholder="Nom du produit"
          />
        </div>
        <NavigationLinkBtn to="/items/create" label="Créer un produit" />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-4/12" />
          <col className="w-3/12" />
          <col className="w-3/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th key={0} className={thClasses}>
              Nom
            </th>
            <th key={1} className={thClasses}>
              Prix
            </th>
            <th key={2} className={thClasses}>
              Stock
            </th>
            <th key={3} className={thClasses}>
              Modifier
            </th>
            <th key={4} className={thClasses}>
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {items?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.displayName} </td>
                <td className={centeredTdClasses}> {row.price} </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <ToggleSwitch
                      isOn={row.haveStock}
                      onToggle={() => {
                        toggleStock({
                          itemId: row._id,
                          haveStock: !row.haveStock,
                        });
                      }}
                    />
                  </div>
                </td>
                <td className={tdClasses}>
                  <Link className="w-full flex justify-center" to={row._id}>
                    <FaEdit className="w-8 text-primary" />{" "}
                  </Link>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: items?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteItem}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer {itemToDelete?.displayName} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ItemsWrapper;
